'use client';

import React, { useEffect, useState } from 'react';
import { AlertTriangle, Clock, ExternalLink, CheckCircle2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface Alert {
  id: string;
  type: 'pending_review' | 'overdue';
  message: string;
  time: string;
  programme: string;
}

const alertConfig = {
  pending_review: {
    icon: CheckCircle2,
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    iconClass: 'text-blue-500',
    dot: 'bg-blue-500',
  },
  overdue: {
    icon: Clock,
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    iconClass: 'text-amber-500',
    dot: 'bg-amber-500',
  },
};

export default function AlertsPanel() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchAlerts() {
      try {
        const supabase = createClient();

        // Fetch documents awaiting review
        const { data: pendingDocs } = await supabase
          .from('document_records')
          .select('id, doc_type, updated_at, submission_id, student_submissions(programme, student_id, user_profiles(first_name, last_name))')
          .eq('doc_status', 'uploaded')
          .order('updated_at', { ascending: false })
          .limit(5);

        // Fetch overdue submissions (pending for more than 7 days)
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const { data: overdueSubs } = await supabase
          .from('student_submissions')
          .select('id, programme, created_at, student_id, user_profiles(first_name, last_name)')
          .in('overall_status', ['pending', 'under_review'])
          .lt('created_at', sevenDaysAgo.toISOString())
          .order('created_at', { ascending: true })
          .limit(5);

        const built: Alert[] = [];

        (pendingDocs || []).forEach((doc: any) => {
          const sub = Array.isArray(doc.student_submissions) ? doc.student_submissions[0] : doc.student_submissions;
          const profile = sub?.user_profiles ? (Array.isArray(sub.user_profiles) ? sub.user_profiles[0] : sub.user_profiles) : null;
          const name = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : 'Student';
          const when = doc.updated_at
            ? new Date(doc.updated_at).toLocaleDateString('en-JM', { day: '2-digit', month: 'short' })
            : '';
          built.push({
            id: `pend-${doc.id}`,
            type: 'pending_review',
            message: `${name} — ${(doc.doc_type || 'Document').replace(/_/g, ' ')} awaiting review`,
            time: when,
            programme: sub?.programme || 'Unknown Programme',
          });
        });

        (overdueSubs || []).forEach((sub: any) => {
          const profile = sub.user_profiles ? (Array.isArray(sub.user_profiles) ? sub.user_profiles[0] : sub.user_profiles) : null;
          const name = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : 'Student';
          const when = sub.created_at
            ? new Date(sub.created_at).toLocaleDateString('en-JM', { day: '2-digit', month: 'short' })
            : '';
          built.push({
            id: `ov-${sub.id}`,
            type: 'overdue',
            message: `${name} — submission overdue, awaiting review`,
            time: when,
            programme: sub.programme || 'Unknown Programme',
          });
        });

        setAlerts(built);
      } catch {
        setAlerts([]);
      } finally {
        setLoading(false);
      }
    }
    fetchAlerts();
  }, []);

  return (
    <div className="bg-card border border-border rounded-xl shadow-card">
      <div className="flex items-center justify-between px-4 py-3.5 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-red-100 flex items-center justify-center">
            <AlertTriangle size={14} className="text-red-600" />
          </div>
          <span className="text-sm font-semibold text-foreground">Urgent Alerts</span>
        </div>
        <span className="text-xs font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">
          {loading ? '…' : alerts.length}
        </span>
      </div>
      {loading ? (
        <div className="px-4 py-8 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-red-300 border-t-red-600 rounded-full animate-spin" />
        </div>
      ) : alerts.length === 0 ? (
        <div className="px-4 py-8 flex flex-col items-center justify-center text-center">
          <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center mb-3">
            <AlertTriangle size={18} className="text-green-400" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">No urgent alerts</p>
          <p className="text-xs text-muted-foreground mt-1">All submissions are on track</p>
        </div>
      ) : (
        <div className="divide-y divide-border">
          {alerts.map((a) => {
            const cfg = alertConfig[a.type];
            const AlertIcon = cfg.icon;
            return (
              <div
                key={a.id}
                className={`px-4 py-3 ${cfg.bg} hover:opacity-90 transition-opacity cursor-pointer`}
              >
                <div className="flex items-start gap-2.5">
                  <AlertIcon size={14} className={`${cfg.iconClass} flex-shrink-0 mt-0.5`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-foreground font-medium leading-relaxed">{a.message}</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[10px] text-muted-foreground">{a.programme}</span>
                      <span className="text-[10px] font-mono text-muted-foreground">{a.time}</span>
                    </div>
                  </div>
                  <ExternalLink size={12} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="px-4 py-3 border-t border-border">
        <button
          onClick={() => window.location.href = '/admin-dashboard'}
          className="w-full text-xs font-semibold text-red-600 hover:text-red-700 transition-colors flex items-center justify-center gap-1.5 py-1"
        >
          <AlertTriangle size={12} />
          View all alerts
        </button>
      </div>
    </div>
  );
}